/**
 * RestoID — Sign-in (first screen).
 * ---------------------------------------------------------------------------
 * Custom RestoID PRESENTATION on top of the PRISTINE upstream LOGIC:
 *   - guards + settings ......... upstream `useSieMethods` / `useTerms`
 *   - submit / flow routing ..... upstream `useOnSubmit` (passkey-first, SSO,
 *                                 password-vs-code routing, send code, navigate)
 *   - captcha / social / passkey  upstream containers, rendered as-is
 * Only the look is ours, so new Logto fields/logic are absorbed by the sync
 * with no UI work. Swapped in for `./pages/SignIn` via `src/App.tsx`.
 */
import { AgreeToTermsPolicy, ExtraParamsKey, SignInIdentifier, experience } from '@logto/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import WebAuthnContextProvider from '@/Providers/WebAuthnContextProvider';
import GoogleOneTap from '@/components/GoogleOneTap';
import useOnSubmit from '@/components/IdentifierSignInForm/use-on-submit';
import CaptchaBox from '@/containers/CaptchaBox';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import PasskeySignIn from '../components/PasskeySignIn';
import { Terms } from '../components/Terms';
import YandexSignIn from '../components/YandexSignIn';
import {
  BrandHead,
  Button,
  C,
  Divider,
  Field,
  FONT,
  LinkText,
  PhoneInput,
  Stack,
  phoneComplete,
  phoneToIdentifierValue,
} from '../components/restoid';
import useRidTheme from '../components/use-rid-theme';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

/**
 * Identifier form, driven entirely by the upstream submit flow.
 *
 * Phone is the RestoID design's first-class identifier, but it is not the only
 * one a tenant can ask for — the ADMIN tenant signs in with a username, and
 * `useOnSubmit` THROWS when handed an identifier the tenant never declared
 * ("Cannot find method with identifier type ..."). Hard-coding Phone here
 * locked the admin console out the moment this image started serving it.
 * So: render the identifier this tenant actually declares.
 *
 * Everything after this screen is already ours — `onSubmit` routes a username
 * straight to `/sign-in/password`, which is `custom/pages/SignInPassword`, and
 * the MFA step (authenticator code, WebAuthn/fingerprint, backup code) is the
 * custom set too. Only this one step was missing.
 */
const SignInForm = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { signInMethods } = useSieMethods();
  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit(signInMethods);
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  // Prefer phone when offered, so the production tenant is untouched; otherwise
  // take whatever the tenant put first.
  const identifier =
    signInMethods.find((method) => method.identifier === SignInIdentifier.Phone)?.identifier ??
    signInMethods[0]?.identifier;
  const isPhone = identifier === SignInIdentifier.Phone;

  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trimmed = text.trim();
  const valid = isPhone ? phoneComplete(phone) : trimmed.length > 0;
  // Labels come from upstream's own dictionary, not ours, so Logto Custom
  // Phrases keep overriding them without a code change.
  const actionLabel = isPhone ? c('getCode') : t('action.continue');

  const submit = useCallback(async () => {
    if (!identifier || !valid || submitting) {
      return;
    }
    clearErrorMessage();

    // Match upstream: when the policy is Manual, confirm terms before signing in.
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(identifier, isPhone ? phoneToIdentifierValue(phone) : trimmed);
    } finally {
      setSubmitting(false);
    }
  }, [
    identifier,
    isPhone,
    valid,
    submitting,
    clearErrorMessage,
    agreeToTermsPolicy,
    termsValidation,
    onSubmit,
    phone,
    trimmed,
  ]);

  // Hide the form while the upstream SSO sub-form takes over.
  if (showSingleSignOnForm) {
    return null;
  }

  // An SSO-only tenant declares no methods at all. Showing a field that the
  // submit would reject is worse than showing none; the SSO entry point lives
  // in the footers below.
  if (!identifier) {
    return null;
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={20}>
        <BrandHead sub={c('tagline')} />
        {isPhone ? (
          <div>
            <label
              style={{
                display: 'block',
                color: C.sub,
                font: `500 13px/1 ${FONT}`,
                marginBottom: 9,
              }}
            >
              {c('phoneLabel')}
            </label>
            <PhoneInput value={phone} onChange={setPhone} danger={Boolean(errorMessage)} />
            {errorMessage ? (
              <div
                role="alert"
                style={{ marginTop: 8, color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}
              >
                {errorMessage}
              </div>
            ) : null}
          </div>
        ) : (
          <Field
            value={text}
            onChange={setText}
            label={identifier === SignInIdentifier.Email ? t('input.email') : t('input.username')}
            autoComplete={identifier === SignInIdentifier.Email ? 'email' : 'username'}
            inputMode={identifier === SignInIdentifier.Email ? 'email' : 'text'}
            danger={Boolean(errorMessage)}
            errorMessage={errorMessage}
          />
        )}
        <CaptchaBox />
        <Button
          htmlType="submit"
          title={actionLabel}
          isLoading={submitting}
          isDisabled={!valid}
          onClick={() => void submit()}
        />
        <Terms action={isPhone ? undefined : actionLabel} />
      </Stack>
    </form>
  );
};

/** Real secondary entry points (create account, social, SSO, passkey), restyled. */
const SignInFooters = () => {
  const ridTheme = useRidTheme();
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { signInMethods, socialConnectors, singleSignOnEnabled, passkeySignIn } = useSieMethods();
  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  if (showSingleSignOnForm) {
    return null;
  }

  const showSocial = signInMethods.length > 0 && socialConnectors.length > 0;
  // Yandex gets the dedicated RestoID button, which the official Suggest widget
  // fades in over (see components/YandexSignIn); any other social connectors keep
  // the upstream list.
  const yandexConnector = socialConnectors.find((connector) => connector.target === 'yandex');
  const otherConnectors = socialConnectors.filter((connector) => connector.target !== 'yandex');
  const showPasskey = Boolean(passkeySignIn?.enabled && passkeySignIn.showPasskeyButton);
  const onlySsoOnly =
    Boolean(singleSignOnEnabled) &&
    signInMethods.length === 0 &&
    socialConnectors.length === 0 &&
    agreeToTermsPolicy === AgreeToTermsPolicy.Manual;

  return (
    <Stack gap={16}>
      {showSocial ? (
        <>
          <Divider label={c('or')} />
          {otherConnectors.length > 0 ? (
            <SocialSignInList socialConnectors={otherConnectors} />
          ) : null}
          {/* Keyed on the theme: the Yandex Suggest SDK has no way to re-theme an
              already-initialised widget, so a theme switch has to remount it. */}
          {yandexConnector ? <YandexSignIn key={ridTheme} connector={yandexConnector} /> : null}
        </>
      ) : null}

      {showPasskey ? <PasskeySignIn /> : null}

      {/* Last, under the buttons. It used to sit ABOVE the social divider, which put
          its lead-in one line above the divider's own "или" — the same word twice in
          60px. Down here it reads as the final alternative and needs no lead-in at
          all, so the link carries the line on its own. */}
      {singleSignOnEnabled ? (
        <>
          <div style={{ textAlign: 'center', color: C.sub, font: `400 14px/1.5 ${FONT}` }}>
            <LinkText onClick={() => void handleSsoNavigation()}>
              {t('action.single_sign_on')}
            </LinkText>
          </div>
          {onlySsoOnly ? <TermsAndPrivacyCheckbox /> : null}
        </>
      ) : null}
    </Stack>
  );
};

const SignIn = () => {
  const { signInMode } = useSieMethods();
  const [params] = useSearchParams();

  const oneTimeToken = useMemo(() => params.get(ExtraParamsKey.OneTimeToken), [params]);

  if (!signInMode) {
    return <ErrorPage />;
  }

  // Registration is hidden — accounts are auto-created on sign-in, so we never
  // bounce to /register (which now redirects back here).

  if (oneTimeToken) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.sign_in_to_your_account' }}>
      <GoogleOneTap context="signin" />
      <WebAuthnContextProvider>
        <SingleSignOnFormModeContextProvider>
          <Stack gap={24}>
            <SignInForm />
            <SignInFooters />
          </Stack>
        </SingleSignOnFormModeContextProvider>
      </WebAuthnContextProvider>
    </FirstScreenLayout>
  );
};

export default SignIn;
